import React, { Fragment, useContext, useEffect, useState, useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, LinearProgress, Typography } from '@mui/material'
import axios from 'axios'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AuthContext } from '../../../context/AuthProvider'
import FormButtonContainer from '../../../components/FormButtonContainer'
import FormStepper from '../../../components/FormStepper'
import PageHeader from '../../../components/PageHeader'
import { LaakariButton, ShadowContainer } from '../../../components/styled/uiComponents'
import { MetadataContext, UserContext } from '../../../context'
import {
  buildApiUrl,
  parseFormData,
  parseNewJobFormData,
  parseMembershipEmploymentFormData,
  universityList
} from '../../../utils/utils'
import FormSentConfirmation from '../FormSentConfirmation'
import { isSessionInvalidError } from '../../../utils/error'
import { isStudent, userFoundInAapeli } from '../../../utils/membershipChecks'
import { UpdateJobFormData } from '../../../types/job'
import EmploymentForm from './EmploymentForm'
// import EmploymentForm2 from './EmploymentForm2'
import MembershipStartsField from './MembershipStartsField'
import PersonalInfo from './PersonalInfo'
import { membershipFormSchema } from './schema'
import Summary from './Summary'

const MembershipForm = () => {
  const [page, setPage] = useState(0)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [employmentSkipped, setEmploymentSkipped] = useState(false)

  const { user } = useContext(UserContext)
  const { metaData } = useContext(MetadataContext)
  const { bearerToken, handleLogout } = useContext(AuthContext)
  const navigate = useNavigate()
  const location = useLocation()

  // Sync page state with URL search params (for browser back/forward within the form)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stepFromUrl = parseInt(params.get('step') || '0', 10)
    setPage(stepFromUrl)
  }, [location.search])

  const currentValidationSchema = membershipFormSchema[page]
  const methods = useForm<any>({
    shouldUnregister: false,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: {
      formType: 'MEMBER', // || 'STUDENTMEMBER',
      organization: {
        name: 'Suomen Lääkäriliitto'
      },
      els_hetu: user?.els_hetu,
      birthdate: user?.birthdate,
      lastname: user?.lastname,
      firstname: user?.firstname,
      membership_starts: 'WHEN_APPROVED',
      address: {
        els_postikieli: JSON.stringify({ attributevalue: 'fi', value: 'Suomi' })
      }
    }
  })

  const {
    register,
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger
  } = methods

  const jobValue = getValues('employment.els_soteorganisaatio')

  useEffect(() => {
    /** go to first employment page if no employment is selected (unless skipped intentionally) */
    if (page === 3 && !jobValue && !employmentSkipped) {
      navigate('/membershipApplication?step=2', { replace: true })
    }
  }, [page, jobValue, employmentSkipped, navigate])
  const pageProps = { register, user, errors, metaData, getValues, control, setValue }

  // Prefill cursus info from Aapeli sidosryhmat when available (student in Aapeli)
  useEffect(() => {
    if (page === 0 && user && Array.isArray(user.sidosryhmat) && isStudent(user)) {
      const now = new Date()
      const activeStudentGroup = user.sidosryhmat.find((sr: any) => {
        const isStudentGroup = sr['paataso.els_paataso'] === 'Lääketieteen opiskelijat'
        const end = sr['els_paattymispaiva'] ? new Date(sr['els_paattymispaiva']) : null
        return isStudentGroup && (!end || end > now)
      })

      if (activeStudentGroup) {
        const alkupaiva: string | undefined = activeStudentGroup['els_alkupaiva']
        if (alkupaiva) {
          const d = new Date(alkupaiva)
          const year = d.getFullYear()
          const juneFirst = new Date(year, 5, 1)
          const semesterName = d < juneFirst ? 'Kevät' : 'Syksy'

          setValue('cursus.els_aloitusvuosi', `${year}`)

          const semester = metaData?.lukukaudet?.find(
            (opt: any) => opt?.value?.toLowerCase?.() === semesterName.toLowerCase()
          )
          if (semester?.attributevalue && semester?.value) {
            setValue(
              'cursus.els_lukukausi',
              JSON.stringify({ attributevalue: semester.attributevalue, value: semester.value })
            )
          }
        }

        const tarkenne: string | undefined = activeStudentGroup['tarkenne.els_sidosryhmantarkenne']
        if (tarkenne) {
          const parts = tarkenne.split(' - ')
          const schoolName = parts[parts.length - 1]?.trim()
          if (schoolName) {
            const uni = universityList.find(
              u => u.els_koodinnimi?.toLowerCase?.() === schoolName.toLowerCase()
            )
            if (uni) {
              setValue(
                'cursus.els_oppilaitos',
                JSON.stringify({ els_koodiid: uni.els_koodiid, els_koodinnimi: uni.els_koodinnimi })
              )
            }
          }
        }
      }
    }
  }, [page, user, metaData, setValue])

  // Re-validate els_loppupvm when els_alkupvm changes
  const elsAlkupvm = watch('employment.els_alkupvm')
  useEffect(() => {
    if (elsAlkupvm && /^\d{4}-\d{2}-\d{2}$/.test(elsAlkupvm)) {
      trigger('employment.els_loppupvm')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elsAlkupvm])

  const handleNext = async (e: any) => {
    e.preventDefault()

    const isStepValid = await trigger()
    // Debug: Log current form values and page
    console.log('Current page:', page)
    console.log('Current form values:', getValues())
    console.log('Validation result:', isStepValid)
    console.log('Form errors:', errors)

    // Specifically check employment values if on page 2 AND validation passed
    if (page === 2 && isStepValid) {
      const employmentValuesRaw = getValues('employment')

      const employmentValues = parseMembershipEmploymentFormData(employmentValuesRaw)
      setValue('employmentClean', employmentValues)
      console.log('Employment values:', employmentValues)
      console.log('els_soteorganisaatio value:', employmentValues?.els_soteorganisaatio)
      console.log('els_tyosuhdesektori value:', employmentValues?.els_tyosuhdesektori)
      console.log('sll_tyosuhde_sijaintikunta value:', employmentValues?.sll_tyosuhde_sijaintikunta)
    }

    // Additional comprehensive check for employment start date on employment page (page 2)
    if (page === 2) {
      const employmentStartDate = getValues('employment.els_alkupvm')
      console.log(
        'Checking employment start date:',
        employmentStartDate,
        'type:',
        typeof employmentStartDate
      )

      // Check if the date is null, undefined, or empty
      if (
        !employmentStartDate ||
        employmentStartDate === '' ||
        employmentStartDate === null ||
        employmentStartDate === undefined
      ) {
        console.log('Employment start date is null/empty, preventing navigation')
        return
      }

      // Check if the date format is valid (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (!dateRegex.test(employmentStartDate)) {
        console.log('Employment start date has invalid format, preventing navigation')
        return
      }

      // Check if it's actually a valid date
      const selectedDate = new Date(employmentStartDate)
      if (isNaN(selectedDate.getTime())) {
        console.log('Employment start date is invalid, preventing navigation')
        return
      }

      // Check for date component mismatch (e.g., 2023-02-30)
      const [year, month, day] = employmentStartDate.split('-').map(Number)
      if (
        selectedDate.getFullYear() !== year ||
        selectedDate.getMonth() !== month - 1 ||
        selectedDate.getDate() !== day
      ) {
        console.log('Employment start date components mismatch, preventing navigation')
        return
      }

      // Check if there are specific errors on the employment start date field
      if ((errors.employment as any)?.els_alkupvm) {
        console.log(
          'Employment start date has validation errors, preventing navigation:',
          (errors.employment as any)?.els_alkupvm
        )
        return
      }

      console.log('Employment start date validation passed, allowing navigation')
    }

    if (isStepValid) {
      const nextPage = page + 1
      navigate(`/membershipApplication?step=${nextPage}`)
    } else {
      console.log('Validation failed, staying on current page')
    }
  }

  const handleBack = useCallback(
    (e: any) => {
      e.preventDefault()
      if (page > 0) {
        // Navigate to previous step within the form
        const prevPage = page - 1
        const newUrl =
          prevPage === 0 ? '/membershipApplication' : `/membershipApplication?step=${prevPage}`
        navigate(newUrl)
      } else {
        // On first page, go back to studentApplication
        navigate('/studentApplication')
      }
    },
    [page, navigate]
  )

  const onSubmit = async (data: any) => {
    try {
      data.employment = data.employmentClean
      delete data.employmentClean
      // Ensure postal language is properly formatted
      if (data?.address?.els_postikieli) {
        try {
          // Try to parse the value - it should be a JSON string
          const parsedPostikieli = JSON.parse(data.address.els_postikieli)
          // Ensure attributevalue is set correctly (API might expect number 358 for Finnish)
          if (parsedPostikieli && typeof parsedPostikieli === 'object') {
            parsedPostikieli.attributevalue = 358
            data.address.els_postikieli = JSON.stringify(parsedPostikieli)
          }
        } catch (parseError) {
          // If parsing fails, log the error but don't break the submission
          // The value might already be in the correct format or might need to be set from metadata
          console.warn(
            'Failed to parse postal language value:',
            data.address.els_postikieli,
            parseError
          )
          // Try to set a default value if parsing fails
          const defaultPostikieli = metaData?.postikielet?.find(
            (lang: any) => lang.value === 'Suomi'
          )
          if (defaultPostikieli) {
            data.address.els_postikieli = JSON.stringify({
              attributevalue: 358,
              value: defaultPostikieli.value
            })
          }
        }
      }
      const parsedData = parseFormData(data)
      const url = buildApiUrl('membership-application')
      await axios.post(url, parsedData, {
        headers: { 'x-tietoni-token': `Bearer ${bearerToken}` }
      })
      setFormSubmitted(true)
    } catch (error) {
      console.log('error when submitting form', error)
      if (isSessionInvalidError(error)) {
        toast.error('Istunto on päättynyt. Kirjaudu uudelleen sisään.')
        handleLogout(() => navigate('/'))
      } else {
        toast.error('Virhe lomakkeen lähetyksessä. Yritä uudelleen.')
      }
    }
  }

  return metaData && user ? (
    <Fragment>
      {formSubmitted ? (
        <FormSentConfirmation formName='Liity Lääkäriliiton jäseneksi!' />
      ) : (
        <Fragment>
          <PageHeader />
          <div style={{ padding: '3vh' }}>
            <Typography variant='h3' color='primary'>
              Liity Lääkäriliiton jäseneksi
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Grid item xs={12} sx={{ paddingTop: '2vh' }}>
                  <FormStepper activeStep={page} stepsCount={4} />
                </Grid>
                {page === 3 && <p>Olet lähettämässä lomaketta seuraavilla tiedoilla: </p>}
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <ShadowContainer>
                      {page === 0 && <PersonalInfo watch={watch} {...pageProps} />}
                      {page === 1 && <MembershipStartsField {...pageProps} />}
                      {page === 2 && (
                        <EmploymentForm
                          skipEmployment={() => {
                            // Clear all employment values when skipping
                            setValue('employment', undefined)
                            setValue('employmentClean', undefined)
                            setEmploymentSkipped(true)
                            navigate('/membershipApplication?step=3')
                          }}
                          watch={watch}
                          {...pageProps}
                        />
                      )}
                      {page === 3 && <Summary formState={getValues()} />}
                    </ShadowContainer>

                    <Grid
                      container
                      spacing={3}
                      direction='row'
                      wrap='wrap-reverse'
                      alignItems='center'
                      justifyContent='space-between'
                      sx={{ paddingTop: '3vh' }}>
                      {page === 0 && (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='left'>
                            <LaakariButton
                              variant='outlined'
                              onClick={() => navigate('/studentApplication')}>
                              Takaisin
                            </LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      )}

                      {page !== 0 && (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='left'>
                            <LaakariButton
                              variant='outlined'
                              disabled={page === 0}
                              onClick={handleBack}>
                              Takaisin
                            </LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      )}
                      {page !== 3 ? (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='right'>
                            <LaakariButton onClick={handleNext}>Jatka</LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      ) : (
                        <Grid item xs={12} md={4}>
                          <FormButtonContainer align='right'>
                            <LaakariButton disabled={isSubmitting} type='submit'>
                              Lähetä
                            </LaakariButton>
                          </FormButtonContainer>
                        </Grid>
                      )}
                    </Grid>
                  </form>
                </FormProvider>
              </Grid>
            </Grid>
          </div>
        </Fragment>
      )}
    </Fragment>
  ) : (
    <LinearProgress />
  )
}

export default MembershipForm
